// ghostTrack.ts — GHOST DUELS (Slice A) timed-track codec. Framework-free + DOM-free (no Phaser, no
// window, no core import), so it round-trips in a plain `tsx` smoke exactly like ghostRoute.ts.
//
// A ghost track is the player-0 EVENT stream of a finished board, replayed tick-accurately by a
// spectral render entity (ghostRacer.ts). We record the OBSERVED event log — never a re-sim — because
// human input is wall-time injected, so a seed+command re-sim is not bit-reproducible; a position/
// event track is determinism-independent and fail-closed by construction (GHOST_DUELS_BRIEF §1).
//
// WIRE FORMAT (base64url of a byte stream; all multi-byte ints are unsigned LEB128 varints):
//   [0]         version byte (= 1)
//   then, per event, in log order:
//     varint    Δt   — quantized-50ms delta from the previous event (first Δ is from t=0)
//     1 byte    tag  — low 3 bits: 0 move · 1 passArm · 2 munchCorrect · 3 munchDecoy · 4 gauntlet
//     varint    cell — grid cell id (0..35)
//     varint    val  — ZIGZAG signed score delta, PRESENT ONLY for the scoring tags 2 & 3
// The value is omitted for move/passArm/gauntlet. `gauntlet` is a MARKER (the THREADED ×1.5 beat)
// whose log `value` duplicates the munchCorrect it rides with, so it is NEVER summed into finalScore —
// only munchCorrect + munchDecoy values are (GHOST_DUELS_BRIEF §2).
//
// decodeTrack() is FAIL-CLOSED: any corruption (bad base64url, wrong version, unknown tag, truncated
// varint) returns null — never throws, never yields a partial event. An empty track (a version byte
// with no events) is a VALID empty GhostTrack, distinct from null.

/** The five player-0 event kinds a ghost replays. */
export type GhostEventKind = 'move' | 'passArm' | 'munchCorrect' | 'munchDecoy' | 'gauntlet';

/** One decoded ghost event. `value` is present only for the scoring kinds (munchCorrect/munchDecoy). */
export interface GhostEvent {
  /** ms since board start, quantized to QUANTUM_MS. */
  t: number;
  kind: GhostEventKind;
  cell: number;
  value?: number;
}

/** A fully decoded ghost track: the replay events + derived duration + cumulative score. */
export interface GhostTrack {
  events: GhostEvent[];
  /** ms of the last event (0 for an empty track). */
  durationMs: number;
  /** sum of the scoring events' values (integer). */
  finalScore: number;
}

/**
 * Minimal structural shape of a core `GameEvent` (t/type/player/cell/value). Declared locally so this
 * codec stays core-free and smoke-testable; a `readonly GameEvent[]` is structurally assignable to it.
 */
export interface TrackSourceEvent {
  t: number;
  type: string;
  player?: number;
  cell?: number;
  value?: number;
}

const VERSION = 1;
/** Δt quantum: 50ms. A ~80ms step-grid move rounds to 1–2 quanta; a 40s board fits in ~800 quanta. */
export const QUANTUM_MS = 50;

const KIND_TAG: Record<GhostEventKind, number> = {
  move: 0,
  passArm: 1,
  munchCorrect: 2,
  munchDecoy: 3,
  gauntlet: 4,
};
const TAG_KIND: readonly (GhostEventKind | undefined)[] = [
  'move',
  'passArm',
  'munchCorrect',
  'munchDecoy',
  'gauntlet',
];
/** The event types we record (everything else in the player-0 stream is ignored). */
const RECORDED = new Set<string>(['move', 'passArm', 'munchCorrect', 'munchDecoy', 'gauntlet']);

function isScoring(kind: GhostEventKind): boolean {
  return kind === 'munchCorrect' || kind === 'munchDecoy';
}

// URL-safe base64url alphabet (A–Z a–z 0–9 - _), matching ghostRoute.ts.
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const INV: Int16Array = (() => {
  const t = new Int16Array(128).fill(-1);
  for (let i = 0; i < ALPHABET.length; i += 1) t[ALPHABET.charCodeAt(i)] = i;
  return t;
})();

// --- varint + zigzag (unsigned LEB128) ------------------------------------------------------------

function writeUVarint(out: number[], value: number): void {
  let v = value >>> 0;
  while (v >= 0x80) {
    out.push((v & 0x7f) | 0x80);
    v >>>= 7;
  }
  out.push(v);
}

/** Read an unsigned LEB128 varint. Returns null on truncation / overlong (>5 bytes) — never throws. */
function readUVarint(bytes: Uint8Array, start: number): { value: number; next: number } | null {
  let value = 0;
  let shift = 0;
  let i = start;
  for (;;) {
    if (i >= bytes.length) return null; // truncated
    if (shift > 28) return null; // > 5 bytes → corrupt / overflow guard
    const b = bytes[i];
    i += 1;
    value |= (b & 0x7f) << shift;
    if ((b & 0x80) === 0) break;
    shift += 7;
  }
  return { value: value >>> 0, next: i };
}

function zigzag(n: number): number {
  const v = Math.trunc(n);
  return ((v << 1) ^ (v >> 31)) >>> 0;
}
function unzigzag(u: number): number {
  return (u >>> 1) ^ -(u & 1);
}

// --- base64url over a raw byte array (no Buffer/btoa — browser + tsx safe) -------------------------

function toBase64Url(bytes: number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] & 0xff;
    const b1 = i + 1 < bytes.length ? bytes[i + 1] & 0xff : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] & 0xff : 0;
    const n = (b0 << 16) | (b1 << 8) | b2;
    out += ALPHABET[(n >> 18) & 63];
    out += ALPHABET[(n >> 12) & 63];
    if (i + 1 < bytes.length) out += ALPHABET[(n >> 6) & 63];
    if (i + 2 < bytes.length) out += ALPHABET[n & 63];
  }
  return out;
}

/** Decode base64url to bytes. Returns null on any invalid char / impossible length — never throws. */
function fromBase64Url(str: string): Uint8Array | null {
  const n = str.length;
  if (n % 4 === 1) return null; // impossible base64url length
  const out: number[] = [];
  let acc = 0;
  let accBits = 0;
  for (let i = 0; i < n; i += 1) {
    const code = str.charCodeAt(i);
    const c = code < 128 ? INV[code] : -1;
    if (c < 0) return null; // invalid char (incl. '=' padding)
    acc = (acc << 6) | c;
    accBits += 6;
    if (accBits >= 8) {
      accBits -= 8;
      out.push((acc >> accBits) & 0xff);
      acc &= (1 << accBits) - 1;
    }
  }
  return new Uint8Array(out);
}

// --- public API -----------------------------------------------------------------------------------

/**
 * Build an in-memory GhostTrack from a finished board's event stream. Keeps only the player-0
 * move / passArm / munch / gauntlet events, quantizes each `t` to QUANTUM_MS, and derives durationMs +
 * finalScore. The source is expected in log (time) order and is NOT re-sorted.
 */
export function buildGhostTrack(source: readonly TrackSourceEvent[]): GhostTrack {
  const events: GhostEvent[] = [];
  let finalScore = 0;
  for (const e of source) {
    if (e.player !== 0 || !RECORDED.has(e.type)) continue;
    const kind = e.type as GhostEventKind;
    const t = Math.max(0, Math.round((e.t ?? 0) / QUANTUM_MS) * QUANTUM_MS);
    const cell = Math.max(0, Math.trunc(e.cell ?? 0));
    if (isScoring(kind)) {
      const value = Math.trunc(e.value ?? 0);
      finalScore += value;
      events.push({ t, kind, cell, value });
    } else {
      events.push({ t, kind, cell });
    }
  }
  const durationMs = events.length > 0 ? events[events.length - 1].t : 0;
  return { events, durationMs, finalScore };
}

/** Encode a GhostTrack to a compact base64url string (see the WIRE FORMAT header). */
export function encodeTrack(track: GhostTrack): string {
  const bytes: number[] = [VERSION];
  let prevQ = 0;
  for (const ev of track.events) {
    const q = Math.max(0, Math.round(ev.t / QUANTUM_MS));
    writeUVarint(bytes, Math.max(0, q - prevQ));
    prevQ = q;
    bytes.push(KIND_TAG[ev.kind] & 0x07);
    writeUVarint(bytes, Math.max(0, Math.trunc(ev.cell)));
    if (isScoring(ev.kind)) writeUVarint(bytes, zigzag(ev.value ?? 0));
  }
  return toBase64Url(bytes);
}

/** Convenience: build + encode straight from a raw event stream. */
export function encodeTrackFromEvents(source: readonly TrackSourceEvent[]): string {
  return encodeTrack(buildGhostTrack(source));
}

/**
 * Decode a base64url track string back to a GhostTrack, or null on ANY corruption (fail-closed). A
 * valid but empty track (version byte only) decodes to `{ events: [], durationMs: 0, finalScore: 0 }`.
 */
export function decodeTrack(str: string | null | undefined): GhostTrack | null {
  if (typeof str !== 'string' || str.length === 0) return null;
  const bytes = fromBase64Url(str);
  if (bytes === null || bytes.length < 1) return null;
  if (bytes[0] !== VERSION) return null;

  const events: GhostEvent[] = [];
  let finalScore = 0;
  let cumQ = 0;
  let i = 1;
  while (i < bytes.length) {
    const dq = readUVarint(bytes, i);
    if (dq === null) return null;
    i = dq.next;
    if (i >= bytes.length) return null; // missing tag byte
    const kind = TAG_KIND[bytes[i] & 0x07];
    i += 1;
    if (kind === undefined) return null; // unknown tag → corrupt
    const cellR = readUVarint(bytes, i);
    if (cellR === null) return null;
    i = cellR.next;
    cumQ += dq.value;
    const t = cumQ * QUANTUM_MS;
    if (isScoring(kind)) {
      const vR = readUVarint(bytes, i);
      if (vR === null) return null;
      i = vR.next;
      const value = unzigzag(vR.value);
      finalScore += value;
      events.push({ t, kind, cell: cellR.value, value });
    } else {
      events.push({ t, kind, cell: cellR.value });
    }
  }
  const durationMs = events.length > 0 ? events[events.length - 1].t : 0;
  return { events, durationMs, finalScore };
}
